import { useState, useRef } from "react";
import { QRCodeCanvas } from "qrcode.react";
import Swal from "sweetalert2";
import type { Invitation } from "@/pages/admin/invitations/components/InvitationModal";

interface Props {
  form: Invitation;
  onChange: <K extends keyof Invitation>(key: K, val: Invitation[K]) => void;
  onCopyLink: () => void;
}

function generateSlug(title: string) {
  return (
    title
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9\s-]/g, "")
      .trim()
      .replace(/\s+/g, "-")
      .slice(0, 40) +
    "-" +
    Math.random().toString(36).slice(2, 7)
  );
}

const CHECKLIST = [
  { key: "client_id",       label: "Client assigned",     icon: "ri-user-line" },
  { key: "event_type",      label: "Event type selected", icon: "ri-calendar-event-line" },
  { key: "event_title",     label: "Event title set",     icon: "ri-text" },
  { key: "event_date",      label: "Event date set",      icon: "ri-calendar-2-line" },
  { key: "venue_name",      label: "Venue name added",    icon: "ri-map-pin-line" },
  { key: "cover_image_url", label: "Cover image added",   icon: "ri-image-2-line" },
  { key: "public_slug",     label: "Public URL slug set", icon: "ri-link-m" },
];

/* ─── QR Modal ───────────────────────────────────────────────── */
function QRModal({ url, title, onClose }: { url: string; title: string; onClose: () => void }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const handleDownload = () => {
    // Find the canvas rendered by QRCodeCanvas
    const canvas = document.getElementById("inv-qr-canvas") as HTMLCanvasElement | null;
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = `qr-${title.replace(/\s+/g, "-").toLowerCase()}.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(url);
    Swal.fire({ title: "Copied!", text: url, icon: "success", timer: 1500, showConfirmButton: false });
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center px-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-3xl w-full max-w-sm overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <h3 className="text-slate-800 font-semibold text-base">QR Code</h3>
            <p className="text-slate-400 text-xs mt-0.5">Scan to open the invitation</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-all cursor-pointer"
          >
            <i className="ri-close-line"></i>
          </button>
        </div>

        {/* QR */}
        <div className="px-6 py-8 flex flex-col items-center gap-5">
          <div className="p-4 bg-white border-2 border-slate-100 rounded-2xl">
            <QRCodeCanvas
              id="inv-qr-canvas"
              value={url}
              size={200}
              level="H"
              marginSize={1}
              imageSettings={{
                src: "",
                height: 0,
                width: 0,
                excavate: false,
              }}
            />
          </div>

          {/* URL display */}
          <div className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 flex items-center gap-2">
            <span className="w-3.5 h-3.5 flex items-center justify-center text-slate-400 shrink-0">
              <i className="ri-link-m text-xs"></i>
            </span>
            <p className="text-slate-500 text-xs font-mono flex-1 truncate">{url}</p>
          </div>

          {/* Actions */}
          <div className="w-full grid grid-cols-2 gap-3">
            <button
              onClick={handleDownload}
              className="flex items-center justify-center gap-2 py-3 rounded-xl bg-indigo-500 hover:bg-indigo-600 text-white text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-download-2-line"></i>
              </span>
              Download PNG
            </button>
            <button
              onClick={handleCopyUrl}
              className="flex items-center justify-center gap-2 py-3 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-file-copy-line"></i>
              </span>
              Copy URL
            </button>
          </div>

          <p className="text-slate-400 text-xs text-center">
            Print or share this QR code so guests can open the invitation directly from their phone.
          </p>
        </div>
      </div>
    </div>
  );
}

/* ─── Main component ─────────────────────────────────────────── */
export default function PublishTab({ form, onChange, onCopyLink }: Props) {
  const [qrOpen, setQrOpen] = useState(false);

  const publicUrl = form.public_slug
    ? `${window.location.origin}/invite/${form.public_slug}`
    : "";

  const whatsappUrl = publicUrl
    ? `https://wa.me/?text=${encodeURIComponent(`You're invited! 🎉 Open your invitation here: ${publicUrl}`)}`
    : "";

  const completedCount = CHECKLIST.filter(
    (item) => !!form[item.key as keyof Invitation],
  ).length;
  const readyToPublish = completedCount >= 4 && !!form.public_slug && !!form.client_id;

  const handleCopy = () => {
    if (!publicUrl) return;
    navigator.clipboard.writeText(publicUrl);
    Swal.fire({ title: "Copied!", text: publicUrl, icon: "success", timer: 1500, showConfirmButton: false });
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-slate-800 font-semibold text-sm mb-1">Publish & Share</h3>
        <p className="text-slate-400 text-xs">Configure the public URL, publish the invitation and share it with guests.</p>
      </div>

      {/* ── Readiness checklist ── */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <p className="text-slate-600 text-sm font-medium">Readiness Checklist</p>
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
            completedCount === CHECKLIST.length
              ? "bg-emerald-100 text-emerald-700"
              : completedCount >= 4
              ? "bg-amber-100 text-amber-700"
              : "bg-slate-100 text-slate-500"
          }`}>
            {completedCount}/{CHECKLIST.length}
          </span>
        </div>
        <div className="divide-y divide-slate-50">
          {CHECKLIST.map((item) => {
            const done = !!form[item.key as keyof Invitation];
            return (
              <div key={item.key} className="flex items-center gap-3 px-4 py-2.5">
                <div className={`w-5 h-5 flex items-center justify-center rounded-full shrink-0 ${
                  done ? "bg-emerald-100 text-emerald-600" : "bg-slate-100 text-slate-300"
                }`}>
                  <i className={done ? "ri-check-line text-xs" : "ri-subtract-line text-xs"}></i>
                </div>
                <span className={`w-4 h-4 flex items-center justify-center ${done ? "text-slate-500" : "text-slate-300"}`}>
                  <i className={`${item.icon} text-xs`}></i>
                </span>
                <p className={`text-sm ${done ? "text-slate-600" : "text-slate-300"}`}>{item.label}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Public slug ── */}
      <div>
        <label className="block text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5">
          Public URL Slug <span className="text-red-400">*</span>
        </label>
        <div className="flex gap-2">
          <div className="flex-1 flex items-center bg-white border border-slate-200 rounded-lg overflow-hidden focus-within:border-indigo-400 focus-within:ring-2 focus-within:ring-indigo-100 transition-all">
            <span className="px-3 py-2.5 text-slate-400 text-xs whitespace-nowrap border-r border-slate-200 bg-slate-50">
              /invite/
            </span>
            <input
              type="text"
              value={form.public_slug}
              onChange={(e) =>
                onChange(
                  "public_slug",
                  e.target.value
                    .toLowerCase()
                    .replace(/[^a-z0-9-]/g, "-")
                    .replace(/-+/g, "-"),
                )
              }
              placeholder="ana-carlos-wedding-2025"
              className="flex-1 bg-transparent px-3 py-2.5 text-sm text-slate-700 placeholder-slate-300 outline-none"
            />
          </div>
          <button
            onClick={() => onChange("public_slug", generateSlug(form.title || "invitation"))}
            className="px-3 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-500 text-xs rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            title="Auto-generate slug"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-refresh-line"></i>
            </span>
          </button>
        </div>
      </div>

      {/* ── Publish toggle ── */}
      <div className={`rounded-xl border-2 p-5 transition-all ${
        form.is_published ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-white"
      }`}>
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className={`font-semibold text-sm ${form.is_published ? "text-emerald-700" : "text-slate-700"}`}>
              {form.is_published ? "Invitation is Live" : "Invitation is Draft"}
            </p>
            <p className={`text-xs mt-0.5 ${form.is_published ? "text-emerald-600" : "text-slate-400"}`}>
              {form.is_published
                ? "Guests can access the invitation at the public URL"
                : "Only you can see this invitation right now"}
            </p>
          </div>
          <div
            onClick={() => onChange("is_published", !form.is_published)}
            className={`w-12 h-6 rounded-full transition-colors relative cursor-pointer shrink-0 ${
              form.is_published ? "bg-emerald-500" : "bg-slate-200"
            }`}
          >
            <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
              form.is_published ? "translate-x-6" : "translate-x-0.5"
            }`} />
          </div>
        </div>

        {!readyToPublish && !form.is_published && (
          <div className="flex items-start gap-2 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2.5">
            <span className="w-4 h-4 flex items-center justify-center text-amber-500 shrink-0 mt-0.5">
              <i className="ri-alert-line text-xs"></i>
            </span>
            <p className="text-amber-600 text-xs">
              Complete at least 4 checklist items and set a public slug before publishing.
            </p>
          </div>
        )}
      </div>

      {/* ── Share section (only when slug exists) ── */}
      {publicUrl && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <div className="flex-1 h-px bg-slate-200" />
            <p className="text-slate-400 text-xs font-medium uppercase tracking-wider px-2">Share</p>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          {/* URL bar */}
          <div className="bg-white border border-slate-200 rounded-xl px-4 py-3 flex items-center gap-3">
            <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-100 text-slate-500 shrink-0">
              <i className="ri-link-m text-sm"></i>
            </span>
            <p className="text-slate-600 text-sm font-mono flex-1 truncate min-w-0">{publicUrl}</p>
            <div className="flex items-center gap-1.5 shrink-0">
              <button
                onClick={handleCopy}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-600 text-xs font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                <span className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-file-copy-line"></i>
                </span>
                Copy
              </button>
              <a
                href={publicUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-600 text-xs font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                <span className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-external-link-line"></i>
                </span>
                Open
              </a>
            </div>
          </div>

          {/* Share action buttons */}
          <div className="grid grid-cols-2 gap-3">
            {/* WhatsApp */}
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 px-4 py-3.5 rounded-xl border-2 border-[#25D366]/30 bg-[#25D366]/5 hover:bg-[#25D366]/10 transition-all cursor-pointer group"
            >
              <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-[#25D366] text-white shrink-0">
                <i className="ri-whatsapp-line text-lg"></i>
              </div>
              <div>
                <p className="text-slate-700 font-semibold text-sm">WhatsApp</p>
                <p className="text-slate-400 text-xs">Share via message</p>
              </div>
              <span className="w-4 h-4 flex items-center justify-center text-slate-300 group-hover:text-slate-500 ml-auto transition-colors">
                <i className="ri-arrow-right-s-line"></i>
              </span>
            </a>

            {/* QR Code */}
            <button
              onClick={() => setQrOpen(true)}
              className="flex items-center gap-3 px-4 py-3.5 rounded-xl border-2 border-indigo-100 bg-indigo-50 hover:bg-indigo-100 transition-all cursor-pointer group text-left"
            >
              <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-indigo-500 text-white shrink-0">
                <i className="ri-qr-code-line text-lg"></i>
              </div>
              <div>
                <p className="text-slate-700 font-semibold text-sm">QR Code</p>
                <p className="text-slate-400 text-xs">Download &amp; print</p>
              </div>
              <span className="w-4 h-4 flex items-center justify-center text-slate-300 group-hover:text-slate-500 ml-auto transition-colors">
                <i className="ri-arrow-right-s-line"></i>
              </span>
            </button>
          </div>

          {/* More share options */}
          <div className="grid grid-cols-3 gap-2">
            {/* Copy link */}
            <button
              onClick={handleCopy}
              className="flex flex-col items-center gap-2 py-3.5 px-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 transition-all cursor-pointer group"
            >
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-100 group-hover:bg-slate-200 text-slate-600 transition-colors">
                <i className="ri-links-line text-sm"></i>
              </div>
              <p className="text-slate-600 text-xs font-medium">Copy Link</p>
            </button>

            {/* Email */}
            <a
              href={`mailto:?subject=${encodeURIComponent(`You're invited: ${form.event_title || form.title}`)}&body=${encodeURIComponent(`Hi!\n\nYou're invited to ${form.event_title || form.title}.\n\nOpen your invitation here:\n${publicUrl}`)}`}
              className="flex flex-col items-center gap-2 py-3.5 px-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 transition-all cursor-pointer group"
            >
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-100 group-hover:bg-slate-200 text-slate-600 transition-colors">
                <i className="ri-mail-send-line text-sm"></i>
              </div>
              <p className="text-slate-600 text-xs font-medium">Email</p>
            </a>

            {/* SMS */}
            <a
              href={`sms:?body=${encodeURIComponent(`You're invited! Open your invitation: ${publicUrl}`)}`}
              className="flex flex-col items-center gap-2 py-3.5 px-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 transition-all cursor-pointer group"
            >
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-100 group-hover:bg-slate-200 text-slate-600 transition-colors">
                <i className="ri-message-2-line text-sm"></i>
              </div>
              <p className="text-slate-600 text-xs font-medium">SMS</p>
            </a>
          </div>

          {/* Tip */}
          <div className="flex items-start gap-2.5 bg-indigo-50 border border-indigo-100 rounded-xl px-4 py-3">
            <span className="w-4 h-4 flex items-center justify-center text-indigo-400 shrink-0 mt-0.5">
              <i className="ri-lightbulb-line text-xs"></i>
            </span>
            <p className="text-indigo-600 text-xs leading-relaxed">
              <strong>Tip:</strong> Generate the QR code and print it on physical invitations or place cards so guests can scan and RSVP instantly.
            </p>
          </div>
        </div>
      )}

      {/* ── QR Modal ── */}
      {qrOpen && publicUrl && (
        <QRModal
          url={publicUrl}
          title={form.event_title || form.title || "invitation"}
          onClose={() => setQrOpen(false)}
        />
      )}
    </div>
  );
}
